import { memo, useCallback, useState } from "react";

// Tipovi i konstante
import { WidgetEditProps } from "./types";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { useTranslate } from "helpers";
import { classNames } from "helpers";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { CheckBox } from "ui/components/CheckBox";
import { AvailableWidget } from "ui/components/Grid/types";
import { DASHBOARD_WIDGET_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { GridTemplateConfig, GRID_TEMPLATES } from "ui/components/Grid/templates";

const TemplatePreview: React.FC<{ template: GridTemplateConfig; isActive: boolean }> = ({
  template,
  isActive,
}) => {
  const rows = 2;
  return (
    <div
      className={classNames(
        "w-16 h-12 border-2 rounded p-1 flex flex-col gap-0.5 cursor-pointer transition-colors",
        isActive
          ? "border-blue-500 bg-blue-50"
          : "border-gray-300 bg-white hover:border-gray-400"
      )}
    >
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-0.5 flex-1">
          {template.widths.map((w, colIndex) => (
            <div
              key={colIndex}
              className={classNames(
                "rounded-sm border",
                isActive
                  ? "border-blue-400 bg-blue-200"
                  : "border-gray-300 border-dashed bg-gray-100"
              )}
              style={{ flex: w }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const WidgetEditCard: React.FC<WidgetEditProps> = ({
  availableWidgets,
  userWidgets,
  setUserWidgets,
  userSubWidgets,
  setUserSubWidgets,
  template,
  onTemplateChange,
  show,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslate();

  // Lokalni state za privremene promjene
  const [localUserWidgets, setLocalUserWidgets] =
    useState<string[]>(userWidgets);
  const [localTemplate, setLocalTemplate] = useState(template);
  const [localSubWidgets, setLocalSubWidgets] =
    useState<Record<string, string[]>>(userSubWidgets);

  // Toggle widget u listi userWidgets
  const handleUserWidgetClick = useCallback((widget: AvailableWidget) => {
    setLocalUserWidgets((prev) =>
      prev.includes(widget.id)
        ? prev.filter((id) => id !== widget.id)
        : [...prev, widget.id]
    );
  }, []);

  // Toggle sub-widget
  const handleSubWidgetClick = useCallback((widgetId: string, subWidgetId: string) => {
    setLocalSubWidgets((prev) => {
      const current = prev[widgetId] || [];
      const updated = current.includes(subWidgetId)
        ? current.filter((id) => id !== subWidgetId)
        : [...current, subWidgetId];
      return { ...prev, [widgetId]: updated };
    });
  }, []);

  const handleSave = useCallback(() => {
    setUserWidgets(localUserWidgets);
    setUserSubWidgets(localSubWidgets);
    onTemplateChange(localTemplate);
    if (onSuccess) onSuccess();
  }, [localUserWidgets, localSubWidgets, localTemplate, onSuccess, setUserWidgets, setUserSubWidgets, onTemplateChange]);

  return (
    <Modal show={show} onHide={onClose} size="small" position="side">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={DASHBOARD_WIDGET_PAGE_TITLE.text}
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex gap-4 h-screen w-full m-auto">
          {/* min-w-0 — bez toga red s predlošcima nametne svoju širinu i popis
              modula (s checkboxovima) izađe izvan modala. */}
          <form noValidate className="text-left flex-1 min-w-0 p-4">
            {/* Layout template picker */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2 block">
                {t("admin.grid.template.title")}
              </p>
              <div className="flex flex-wrap gap-3">
                {GRID_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setLocalTemplate(tpl.id)}
                    className="flex flex-col items-center gap-1"
                  >
                    <TemplatePreview
                      template={tpl}
                      isActive={localTemplate === tpl.id}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Widget list grouped by module */}
            <div className="">
              <p className="text-sm font-medium text-gray-700 mb-2 block">
                {t("admin.modal.list.title")}
              </p>
              {Object.entries(
                availableWidgets.reduce<Record<string, AvailableWidget[]>>((acc, widget) => {
                  const module = widget.module || "Ostalo";
                  if (!acc[module]) acc[module] = [];
                  acc[module].push(widget);
                  return acc;
                }, {})
              ).map(([module, widgets]) => (
                <div key={module} className="mb-3">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-2 bg-slate-50 border border-b-0">
                    {module}
                  </div>
                  {widgets.map((widget) => {
                    const isActive = localUserWidgets.includes(widget.id);
                    return (
                      <div key={widget.id}>
                        <div className="flex items-center justify-between p-3 border mb-[-1px]">
                          <div>
                            <span className="text-sm">{widget.name}</span>
                            {widget.description && (
                              <div className="text-sm text-slate-500 ">{widget.description}</div>
                            )}
                          </div>
                          <CheckBox
                            name={`widget-${widget.id}`}
                            checked={isActive}
                            onChange={() => handleUserWidgetClick(widget)}
                          />
                        </div>
                        {isActive && widget.subWidgets && widget.subWidgets.length > 0 && (
                          <div className="border-l-2 border-blue-200 ">
                            {widget.subWidgets.map((sub) => {
                              const subActive = (localSubWidgets[widget.id] || []).includes(sub.id);
                              return (
                                <div
                                  key={sub.id}
                                  className="flex items-center justify-between py-2 px-3 border-b border-r border-t-0 mb-[-1px] bg-slate-25"
                                >
                                  <div className="pl-6">
                                    <span className="text-sm text-slate-900">{sub.name}</span>
                                    {sub.description && (
                                      <div className="text-sm text-slate-500 ">{sub.description}</div>
                                    )}
                                  </div>
                                  <CheckBox
                                    name={`sub-${sub.id}`}
                                    checked={subActive}
                                    onChange={() => handleSubWidgetClick(widget.id, sub.id)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
              <Button variant="outlined" onClick={onClose}>
                {t("admin.close")}
              </Button>
              <Button variant="primary" onClick={handleSave}>
                {t("admin.save")}
              </Button>
            </div>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default memo(WidgetEditCard);
